import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { 
  Play, 
  Search, 
  Clock,
  BookOpen,
  Video,
  FileText,
  X,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Download,
  Film
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'financial', label: 'Financial Freedom' },
  { value: 'mental', label: 'Mental Wellness' },
  { value: 'physical', label: 'Physical Health' },
  { value: 'lifestyle', label: 'Lifestyle' },
];

const mediaTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'video', label: 'Videos' },
  { value: 'lecture', label: 'Lectures' },
  { value: 'document', label: 'Documents' },
];

const sampleMedia = [
  {
    media_id: 'sample_1',
    title: 'Introduction to Financial Freedom',
    description: 'Learn the fundamentals of achieving financial independence and building sustainable wealth.',
    category: 'financial',
    media_type: 'video',
    thumbnail_url: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&q=80',
    file_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '00:10'
  },
  {
    media_id: 'sample_2',
    title: 'Mindfulness for Beginners',
    description: 'A comprehensive guide to starting your mindfulness journey and reducing daily stress.',
    category: 'mental',
    media_type: 'lecture',
    thumbnail_url: 'https://images.unsplash.com/photo-1643184012410-0d0c9070e575?w=600&q=80',
    file_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '00:10'
  },
  {
    media_id: 'sample_3',
    title: 'Movement Without Limits',
    description: 'Discover fitness routines that can be done anywhere, anytime for physical freedom.',
    category: 'physical',
    media_type: 'video',
    thumbnail_url: 'https://images.unsplash.com/photo-1562088287-bde35a1ea917?w=600&q=80',
    file_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '00:10'
  },
  {
    media_id: 'sample_4',
    title: 'Digital Nomad Lifestyle Guide',
    description: 'Everything you need to know about living and working from anywhere in the world.',
    category: 'lifestyle',
    media_type: 'document',
    thumbnail_url: 'https://images.unsplash.com/photo-1602779370210-31cb71dc2ecc?w=600&q=80',
    file_url: null,
    duration: null
  }
];

// Video Player Component
function VideoPlayer({ src, poster, onClose }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative rounded-xl overflow-hidden bg-black">
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="w-full aspect-video"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onClick={togglePlay}
      />
      
      {/* Video Controls Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        {/* Progress Bar */}
        <div 
          className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-3"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-white rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white ml-0.5" />
              )}
            </button>
            <button 
              onClick={toggleMute}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4 text-white" />
              ) : (
                <Volume2 className="w-4 h-4 text-white" />
              )}
            </button>
            <span className="text-white text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          
          <button 
            onClick={toggleFullscreen}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <Maximize className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
      
      {/* Play button overlay when paused */}
      {!isPlaying && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlay}
        >
          <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform">
            <Play className="w-10 h-10 text-gray-800 ml-1" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function MediaPage() {
  const { currentTheme } = useTheme();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [mediaType, setMediaType] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, [category]);

  const fetchMedia = async () => {
    try {
      const params = category !== 'all' ? `?category=${category}` : '';
      const response = await axios.get(`${API}/media${params}`);
      const dbMedia = response.data || [];
      // Combine DB media with sample media if DB is empty
      setMedia(dbMedia.length > 0 ? dbMedia : sampleMedia);
    } catch (error) {
      console.error('Error fetching media:', error);
      setMedia(sampleMedia);
    } finally {
      setLoading(false);
    }
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase()) ||
                          item.description.toLowerCase().includes(search.toLowerCase());
    const matchesType = mediaType === 'all' || item.media_type === mediaType;
    return matchesSearch && matchesType;
  });

  const getMediaIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'lecture': return BookOpen;
      case 'document': return FileText;
      default: return Play;
    }
  };

  const isPlayable = (item) => {
    if (!item) return false;
    return (item.media_type === 'video' || item.media_type === 'lecture') && item.file_url;
  };

  const handleDownload = (item) => {
    if (item.file_url) {
      window.open(item.file_url, '_blank');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-28 pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-medium mb-4" style={{ color: currentTheme.foreground }}>
              Media Library
            </h1>
            <p className="font-body text-lg opacity-70 max-w-2xl">
              Explore our collection of videos, lectures, and resources to support your journey to freedom.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-40" />
              <Input
                placeholder="Search media..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12"
                data-testid="media-search"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full md:w-[200px] h-12" data-testid="media-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={mediaType} onValueChange={setMediaType}>
              <SelectTrigger className="w-full md:w-[200px] h-12" data-testid="media-type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {mediaTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Media Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="skeleton h-80 rounded-2xl" />
              ))}
            </div>
          ) : filteredMedia.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMedia.map((item) => {
                const Icon = getMediaIcon(item.media_type);
                return (
                  <div
                    key={item.media_id}
                    className="media-card group cursor-pointer"
                    onClick={() => setSelectedMedia(item)}
                    data-testid={`media-card-${item.media_id}`}
                  >
                    <div className="relative aspect-video">
                      <img
                        src={item.thumbnail_url || 'https://images.unsplash.com/photo-1602779370210-31cb71dc2ecc?w=600&q=80'}
                        alt={item.title}
                        className="w-full h-full object-cover rounded-2xl"
                      />
                      <div className="overlay rounded-2xl" />
                      <div className="play-btn w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                        <Icon className="w-8 h-8" style={{ color: currentTheme.primary }} />
                      </div>
                      {item.duration && (
                        <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-black/60 text-white text-sm flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {item.duration}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium capitalize"
                          style={{ backgroundColor: `${currentTheme.primary}20`, color: currentTheme.primary }}
                        >
                          {item.category}
                        </span>
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium capitalize"
                          style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
                        >
                          {item.media_type}
                        </span>
                      </div>
                      <h3 className="font-display text-lg font-medium mb-2" style={{ color: currentTheme.foreground }}>
                        {item.title}
                      </h3>
                      <p className="font-body text-sm opacity-60 line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Film className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
                No media found
              </h3>
              <p className="font-body opacity-60">
                Try adjusting your search or filters
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Media Detail Dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{selectedMedia?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Video/Audio Player or Image */}
            {isPlayable(selectedMedia) ? (
              <VideoPlayer 
                src={selectedMedia?.file_url?.startsWith('/') ? `${BACKEND_URL}${selectedMedia.file_url}` : selectedMedia?.file_url}
                poster={selectedMedia?.thumbnail_url}
              />
            ) : (
              <div className="relative aspect-video rounded-xl overflow-hidden">
                <img
                  src={selectedMedia?.thumbnail_url || 'https://images.unsplash.com/photo-1602779370210-31cb71dc2ecc?w=600&q=80'}
                  alt={selectedMedia?.title}
                  className="w-full h-full object-cover"
                />
                {selectedMedia?.media_type === 'document' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Button
                      onClick={() => handleDownload(selectedMedia)}
                      className="gap-2"
                      style={{ backgroundColor: currentTheme.primary }}
                    >
                      <Download className="w-5 h-5" />
                      Download Document
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium capitalize"
                style={{ backgroundColor: `${currentTheme.primary}20`, color: currentTheme.primary }}
              >
                {selectedMedia?.category}
              </span>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium capitalize"
                style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
              >
                {selectedMedia?.media_type}
              </span>
              {selectedMedia?.duration && (
                <span className="flex items-center gap-1 text-sm opacity-60">
                  <Clock className="w-4 h-4" />
                  {selectedMedia.duration}
                </span>
              )}
            </div>
            <p className="font-body leading-relaxed opacity-80">
              {selectedMedia?.description}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
